import React, { useState, useEffect } from 'react'
import { Settings, Bug, AlertCircle, Check, X, Search } from 'lucide-react'
import { apiClient } from '@/utils/api'
import toast from 'react-hot-toast'

interface Family {
  id: string
  name: string
  slug: string
  debug_enabled: boolean
  created_at: string
}

export const DebugSettingsManager: React.FC = () => {
  const [families, setFamilies] = useState<Family[]>([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [updating, setUpdating] = useState<string | null>(null)

  useEffect(() => {
    loadFamilies()
  }, [])

  const loadFamilies = async () => {
    try {
      setLoading(true)
      // TODO: Update this endpoint when backend implements debug_enabled field
      const data = await apiClient.get('/api/platform/families')
      setFamilies(data?.families || [])
    } catch (error) {
      console.error('Failed to load families:', error)
      toast.error('Failed to load families')
    } finally {
      setLoading(false)
    }
  }

  const toggleDebug = async (familyId: string, currentStatus: boolean) => {
    try {
      setUpdating(familyId)
      // TODO: Implement backend endpoint for debug toggle
      await apiClient.post(`/api/platform/families/${familyId}/debug`, {
        enabled: !currentStatus
      })

      // Update local state
      setFamilies(families.map(f =>
        f.id === familyId ? { ...f, debug_enabled: !currentStatus } : f
      ))

      toast.success(`Debug ${!currentStatus ? 'enabled' : 'disabled'} for family`)
    } catch (error) {
      console.error('Failed to toggle debug:', error)
      toast.error('Failed to update debug setting')
    } finally {
      setUpdating(null)
    }
  }

  const filteredFamilies = families.filter(f =>
    f.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    f.slug.toLowerCase().includes(searchQuery.toLowerCase())
  )

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Debug Settings</h2>
        <p className="text-gray-600">
          Enable or disable debug logging for families. Debug logs can be viewed in the browser console.
        </p>
      </div>

      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5" />
          <div className="flex-1">
            <h3 className="font-medium text-blue-900">How Debug Mode Works</h3>
            <p className="text-sm text-blue-800 mt-1">
              When enabled, debug mode activates console logging in the browser for specific components.
              This helps troubleshoot production issues without redeploying code. Users will need to open
              their browser console to see the debug output.
            </p>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search families..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Families Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Family
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Slug
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Debug Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredFamilies.length === 0 ? (
              <tr>
                <td colSpan={4} className="px-6 py-8 text-center text-gray-500">
                  {searchQuery ? 'No families found matching your search' : 'No families found'}
                </td>
              </tr>
            ) : (
              filteredFamilies.map(family => (
                <tr key={family.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="font-medium text-gray-900">{family.name}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-600">{family.slug}.housepoints.ai</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {family.debug_enabled ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        <Check className="h-3 w-3 mr-1" />
                        Enabled
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                        <X className="h-3 w-3 mr-1" />
                        Disabled
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <button
                      onClick={() => toggleDebug(family.id, family.debug_enabled)}
                      disabled={updating === family.id}
                      className={`
                        px-4 py-2 rounded-lg font-medium transition-colors
                        ${family.debug_enabled
                          ? 'bg-red-100 text-red-700 hover:bg-red-200'
                          : 'bg-green-100 text-green-700 hover:bg-green-200'
                        }
                        ${updating === family.id ? 'opacity-50 cursor-not-allowed' : ''}
                      `}
                    >
                      {updating === family.id ? (
                        <span className="flex items-center">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2"></div>
                          Updating...
                        </span>
                      ) : (
                        family.debug_enabled ? 'Disable Debug' : 'Enable Debug'
                      )}
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Debug Components List */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold mb-4">Components with Debug Support</h3>
        <div className="space-y-2">
          <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
            <Bug className="h-5 w-5 text-gray-600" />
            <div>
              <div className="font-medium text-gray-900">School Calendar View</div>
              <div className="text-sm text-gray-600">
                LocalStorage key: <code className="px-2 py-1 bg-gray-200 rounded text-xs">debug_school_calendar</code>
              </div>
            </div>
          </div>

          <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p className="text-sm text-yellow-800">
              <strong>Note:</strong> Currently, debug flags are controlled via localStorage. Backend support for
              per-family debug settings is coming soon. For now, users can enable debug mode manually by opening
              the browser console and running: <code className="px-2 py-1 bg-yellow-100 rounded">localStorage.setItem('debug_school_calendar', 'true')</code>
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
